import { Box, Button, Flex, Progress, Stack, Text } from "@chakra-ui/react";
import type { StatusVariant } from "../components/StatusVariant";
import type { FocusProgress, FocusSubject } from "./focusSubject";

interface Props {
  heading: string;
  subjects: FocusSubject[];
  activeId: string;
  onSelect: (id: string) => void;
  progress?: FocusProgress;
}

const DOT_COLOR: Record<StatusVariant, string> = {
  ok: "smSuccess",
  issue: "smError",
  neutral: "smStatusNeutralAccent",
};

export const FocusSubjectList = ({ heading, subjects, activeId, onSelect, progress }: Props) => (
  <Flex direction="column" h="full" minH="0">
    <Text
      px="4"
      pt="4"
      pb="2"
      fontSize="xs"
      fontWeight="bold"
      letterSpacing="wide"
      textTransform="uppercase"
      color="smMuted"
    >
      {heading}
    </Text>

    <Stack as="nav" aria-label={heading} spacing="1" px="3" pb="2" flex="1" minH="0" overflowY="auto">
      {subjects.map((subject) => {
        const isActive = subject.id === activeId;
        return (
          <Button
            key={subject.id}
            onClick={() => onSelect(subject.id)}
            aria-current={isActive ? "true" : undefined}
            variant="unstyled"
            h="auto"
            py="3"
            px="3"
            rounded="lg"
            borderWidth="1px"
            borderColor={isActive ? "smPrimary" : "transparent"}
            bg={isActive ? "smSuccessSoft" : subject.isSetAside ? "smBackgroundSecondary" : "transparent"}
            _hover={{ bg: isActive ? "smSuccessSoft" : "smBackground" }}
            textAlign="left"
            fontWeight="normal"
            whiteSpace="normal"
          >
            <Flex align="center" gap="3">
              <Text
                flex="1"
                minW="0"
                fontSize="sm"
                fontWeight="semibold"
                color="smText"
                noOfLines={1}
              >
                {subject.title}
              </Text>
              <Box
                as="span"
                aria-hidden
                w="2.5"
                h="2.5"
                rounded="full"
                flexShrink={0}
                bg={DOT_COLOR[subject.status]}
              />
            </Flex>
            <Text mt="1" fontSize="sm" color="smMuted" noOfLines={1}>
              {subject.detail}
            </Text>
          </Button>
        );
      })}
    </Stack>

    {progress && (
      <Box px="4" py="4" borderTopWidth="1px" borderColor="smBorder">
        <Progress value={progress.value} size="xs" rounded="full" colorScheme="teal" />
        <Text mt="2" fontSize="xs" color="smMuted">
          {progress.label}
        </Text>
      </Box>
    )}
  </Flex>
);
